/**
 * Cache reducer function
 */

import type { CacheState, CacheAction, CacheKey } from "../types";
import { parseCacheKey } from "../utils";

export function cacheReducer(state: CacheState, action: CacheAction): CacheState {
  switch (action.type) {
    case "SET_ENTRY": {
      const newEntries = new Map(state.entries);

      // Add new entry
      newEntries.set(action.key, action.entry);

      // Enforce max size by removing oldest entries
      if (newEntries.size > state.maxSize) {
        const sortedEntries = Array.from(newEntries.entries()).sort(
          ([, a], [, b]) => a.timestamp - b.timestamp
        );

        const entriesToRemove = sortedEntries.slice(0, newEntries.size - state.maxSize);
        entriesToRemove.forEach(([key]) => newEntries.delete(key));
      }

      return { ...state, entries: newEntries };
    }

    case "INVALIDATE_ENTRY": {
      const newEntries = new Map(state.entries);
      newEntries.delete(action.key);
      return { ...state, entries: newEntries };
    }

    case "INVALIDATE_PATTERN": {
      const newEntries = new Map(state.entries);
      const pattern = action.pattern;

      for (const [keyString] of newEntries) {
        const key = parseCacheKey(keyString);
        let matches = true;

        if (pattern.action && key.action !== pattern.action) matches = false;
        if (pattern.parentId !== undefined && key.parentId !== pattern.parentId) matches = false;
        if (pattern.driveId !== undefined && key.driveId !== pattern.driveId) matches = false;
        if (pattern.email !== undefined && key.email !== pattern.email) matches = false;
        if (
          pattern.isSecondAccount !== undefined &&
          key.isSecondAccount !== pattern.isSecondAccount
        )
          matches = false;

        if (matches) {
          newEntries.delete(keyString);
        }
      }

      return { ...state, entries: newEntries };
    }

    case "CLEAR_EXPIRED": {
      const now = Date.now();
      const newEntries = new Map(state.entries);

      for (const [key, entry] of newEntries) {
        if (entry.expiresAt < now) {
          newEntries.delete(key);
        }
      }

      return { ...state, entries: newEntries };
    }

    case "CLEAR_ALL": {
      return { ...state, entries: new Map() };
    }

    case "SET_CONFIG": {
      return {
        ...state,
        maxSize: action.maxSize ?? state.maxSize,
        defaultTTL: action.defaultTTL ?? state.defaultTTL,
      };
    }

    default:
      return state;
  }
}
